// ExplainerSubLine — plain-language recipe + source-ref chips for a selected line.
// Shows for: price_method='formula', price_method='calc', or qty_driver set (3-MF type lines).
// Props:
//   line        — full LineRow from getLines()
//   onNavigate  — (nodeId: string) => void — called when a chip is clicked; nodeId is pipe-separated path
//   rmL1, hrL1  — live L1 codes for the RM/HR catalog namespaces, resolved by cost_class in App.jsx
//                 (L1 codes are editable — never hardcode them here, see Q9)

import { useState, useEffect } from 'react';

const TRADE_LABEL = {
  sheet_metal: 'Sheet Metal',
  millwright:  'Millwright',
  electrician: 'Electrician',
  pipefitter:  'Pipefitter',
  ironworker:  'Ironworker',
};

// Fallback for any trade not yet in the map above (e.g. a future 6th trade) —
// capitalize the id rather than render blank.
function tradeLabel(tradeId) {
  if (!tradeId) return '';
  return TRADE_LABEL[tradeId] || (tradeId.charAt(0).toUpperCase() + tradeId.slice(1));
}

function Chip({ label, badge, onClick, title }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: hover ? 'var(--tg-chip-bg-hover)' : 'var(--tg-chip-bg)',
        border: '1px solid var(--tg-chip-border)',
        borderRadius: 4, cursor: 'pointer',
        padding: '1px 6px', fontSize: 'var(--fs-small)', fontWeight: 500,
        color: 'var(--tg-chip-color)', lineHeight: '18px',
      }}
    >
      {label}
      {badge && (
        <span style={{
          fontSize: 'var(--fs-micro)', fontWeight: 700, color: 'var(--tg-chip-badge-color)',
          background: 'var(--tg-chip-badge-bg)', borderRadius: 3,
          padding: '0 3px', lineHeight: '14px',
        }}>
          {badge}
        </span>
      )}
    </button>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ color: 'var(--th-text-hint)', fontSize: 'var(--fs-micro)', fontWeight: 700, minWidth: 54, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </span>
      {children}
    </div>
  );
}

export default function ExplainerSubLine({ line, onNavigate, rmL1 = 'RM', hrL1 = 'HR' }) {
  const [matPath, setMatPath] = useState(null);

  const isCalcFS   = line.price_method === 'calc';
  const isFabType  = line.qty_driver != null;

  useEffect(() => {
    if (!line.material || (!isFabType && !isCalcFS)) return;
    fetch(`/api/catalog/locate?material=${encodeURIComponent(line.material)}`)
      .then(r => r.json())
      .then(path => setMatPath(path))
      .catch(() => {});
  }, [line.material, isFabType, isCalcFS]);

  // The "Recipe" arm went with price_params on 2026-08-18. It described a
  // {base, rate} area recipe read only by PART_SELECT — deleted 2026-08-17 with
  // the parts table — so on all 66 price_method='formula' lines it rendered
  // "formula (params not set)" beside a note saying "(Parts screen only)". The
  // live formula mechanism is `price_expr`, and the Price cell already marks it.
  if (!isCalcFS && !isFabType) return null;

  const matNodeId = matPath
    ? [matPath.l1, matPath.l2, matPath.l3].filter(Boolean).join('|')
    : rmL1;
  const tradeNodeId = `${hrL1}|H1`;

  const baseStyle = {
    display: 'flex', flexDirection: 'column', gap: 5,
    padding: '7px 12px 7px 16px',
    borderTop: '1px dashed var(--th-border-light)',
    fontSize: 'var(--fs-small)', background: 'var(--th-row-selected)',
  };

  if (isCalcFS) {
    return (
      <div style={baseStyle}>
        <Row label="Calc">
          <span style={{ color: 'var(--th-text-hint)', fontStyle: 'italic' }}>FeatureScript computed</span>
          {line.material && (
            <Chip
              label={line.material}
              badge={`/${rmL1}`}
              onClick={() => onNavigate(matNodeId)}
              title={`Open material in ${rmL1} catalog`}
            />
          )}
        </Row>
      </div>
    );
  }

  // 3-MF fabricated type line (qty_driver set)
  const qtyUnit = line.qty_driver === 'area' ? 'm²/hr' : 'kg/hr';
  const fabLabel = tradeLabel(line.fab_trade);
  // Assy row REMOVED (2026-07-04, Alex's decision): shop assembly (inst_rate/
  // inst_trade) is no longer part of the MF fab line cost — it's now pure HR-comp
  // hours at product level. The catalog_lines inst_rate/inst_trade DATA is
  // untouched (dormant); this sub-line just stopped displaying it.

  return (
    <div style={baseStyle}>
      {line.material && (
        <Row label="Material">
          <Chip
            label={line.material}
            badge={`/${rmL1}`}
            onClick={() => onNavigate(matNodeId)}
            title={`Open material in ${rmL1} catalog`}
          />
          {line.unit_conversion != null && line.unit_conversion !== 1 && (
            <span style={{ color: 'var(--tg-chip-badge-color)', fontSize: 'var(--fs-micro)' }}>×{line.unit_conversion} waste</span>
          )}
        </Row>
      )}
      {line.output_rate != null && (
        <Row label="Fab">
          <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)' }}>{line.output_rate} {qtyUnit}</span>
          {fabLabel && (
            <Chip
              label={fabLabel}
              badge={`/${hrL1}`}
              onClick={() => onNavigate(tradeNodeId)}
              title={`Open labor in ${hrL1} catalog`}
            />
          )}
        </Row>
      )}
    </div>
  );
}
